import React, { useEffect } from "react";
import { twMerge } from "tailwind-merge";
import usePaymentMethods from "./hooks/usePaymentMethods";
import { useField } from "formik";
import FormDropdown from "../FormDropdown/FormDropdown";
import { FormInput } from "../FormInput";
import useCryptoInUSD from "@/shared/hooks/useCryptoInUSD";
import usePaymentCurrency from "@/shared/hooks/usePaymentCurrency";

export interface Props {
  icon?: React.ReactNode;
  value?: string;
  autoFocus?: boolean;
  name: string;
  label: string;
  shortInfo?: string;
  tooltipText?: string;
  allThemesWithWhiteBackground?: boolean;
  onChange?: any;
  onPaymentMethodChange?: any;
  onlyErc20Support?: boolean;
  disabled?: boolean;
  disablePaymentMethodChange?: boolean;
  className?: string;
  step?: string;
}

const CurrencyInput = ({
  name,
  label,
  shortInfo,
  tooltipText,
  allThemesWithWhiteBackground = false,
  onChange,
  autoFocus,
  onPaymentMethodChange,
  onlyErc20Support = false,
  disabled,
  className,
  disablePaymentMethodChange,
  step,
  ...props
}: Props) => {
  const currencies = usePaymentMethods(onlyErc20Support);

  const [{ value: priceInCrypto }] = useField(name);
  const [{ value: paymentErc20TokenAddress }] = useField(
    "paymentErc20TokenAddress",
  );

  const paymentMethod = usePaymentCurrency(paymentErc20TokenAddress);

  const { cryptoInUSD: priceInUSD } = useCryptoInUSD(
    priceInCrypto,
    paymentMethod?.id,
  );

  useEffect(() => {
    if (!paymentErc20TokenAddress) {
      return;
    }

    onChange?.(priceInCrypto);
    onPaymentMethodChange?.(paymentErc20TokenAddress);
  }, [
    onChange,
    onPaymentMethodChange,
    paymentErc20TokenAddress,
    priceInCrypto,
  ]);

  return (
    <div>
      <div className="relative">
        <FormInput
          name={name}
          label={label}
          shortInfo={shortInfo || `${priceInUSD || 0} USD`}
          type="number"
          alignCenter={false}
          className={twMerge(
            allThemesWithWhiteBackground && "endemic:focus:border-green",
            "pr - [150px] h - [43px] endemic:text-invert",
            className,
          )}
          labelClassName="night:text-primary"
          onChange={onChange}
          value={priceInCrypto}
          autoFocus={autoFocus}
          tooltipText={tooltipText}
          allThemesWithWhiteBackground={allThemesWithWhiteBackground}
          min={0}
          step={step || "0.0001"}
          disabled={disabled}
          {...props}
        />
        <div
          className={twMerge(
            "absolute flex items-center top-[35px] lg:top-[35px] xl:top-[34.5px]",
            tooltipText ? "right-0 lg:right-[58px]" : " -right-0.5",
          )}
        >
          {currencies ? (
            <FormDropdown
              name="paymentErc20TokenAddress"
              containerClassName="my-0"
              className="w-32 bg-transparent justify-end border-none mr-2 h-1 max-h-[40px] px-4 py-4 text-body-4 border-l-2 rounded-l-none font-semibold text-secondary endemic:text-endemicGray-300 endemic:bg-transparent"
              borderClassName="border-green endemic:border-green"
              childrenClassName="border-green endemic:border-green text-body-4 px-2 py-2 w-50 right-2.5"
              arrowClassName={twMerge(
                "fill-endemicGray-700 endemic:fill-primary",
                allThemesWithWhiteBackground && "endemic:fill-endemicGray-700",
              )}
              options={currencies}
              disabled={disablePaymentMethodChange}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CurrencyInput;
